import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Plus, Box, Upload, Download, FileSpreadsheet, Loader2, Sparkles, Check, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import * as XLSX from 'xlsx';
import { formatCurrency, ITEM_CATEGORIES } from '../lib/constants';
import { Badge } from '../components/ui/badge';

const ItemsPage = () => {
  const { user, hasPermission } = useAuth();
  const [items, setItems] = useState([]);
  const [stockByItem, setStockByItem] = useState({}); // {item_id: {total, byLocation: {code: qty}}}
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showBulkDialog, setShowBulkDialog] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [previewItems, setPreviewItems] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [sortBy, setSortBy] = useState('name_asc');
  const fileInputRef = useRef(null);
  const [form, setForm] = useState({
    name: '',
    category: '',
    unit: '',
    price: 0,
    min_stock: 0,
    is_regular: false,
  });
  const [showAllItems, setShowAllItems] = useState(false);

  useEffect(() => {
    loadItems();
  }, [showAllItems]);

  const loadItems = async () => {
    try {
      const [itemsResp, stockResp] = await Promise.all([
        api.get('/items', { params: { include_all: showAllItems } }),
        api.get('/warehouse/stock').catch(() => ({ data: [] })),
      ]);
      // Sort: new items first, then by name
      const sorted = [...itemsResp.data].sort((a, b) => {
        if (a.is_new && !b.is_new) return -1;
        if (!a.is_new && b.is_new) return 1;
        return (a.name || '').localeCompare(b.name || '');
      });
      // Aggregate stock per item across locations
      const stockMap = {};
      for (const s of stockResp.data) {
        if (!stockMap[s.item_id]) stockMap[s.item_id] = { total: 0, byLocation: {} };
        stockMap[s.item_id].total += (s.quantity || 0);
        if (s.quantity > 0) {
          stockMap[s.item_id].byLocation[s.location_id] = s.quantity;
        }
      }
      setStockByItem(stockMap);
      setItems(sorted);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkReviewed = async (itemId, itemName) => {
    if (!window.confirm(`Tandai "${itemName}" sudah direview?`)) return;
    try {
      await api.post(`/items/${itemId}/mark-reviewed`);
      toast.success('Barang ditandai sudah direview');
      loadItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal');
    }
  };

  const handleDeleteItem = async (itemId, itemName) => {
    if (!window.confirm(`Hapus barang "${itemName}" dari master? Aksi ini tidak dapat dibatalkan.`)) return;
    try {
      await api.delete(`/items/${itemId}`);
      toast.success('Barang berhasil dihapus');
      loadItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menghapus barang');
    }
  };

  const canDeleteItems = user?.role === 'super_admin' || hasPermission('manage_items');

  const handleCreate = async () => {
    try {
      await api.post('/items', {
        ...form,
        price: parseFloat(form.price),
        min_stock: parseInt(form.min_stock),
        is_regular: !!form.is_regular,
      });
      toast.success('Barang berhasil ditambahkan');
      setShowDialog(false);
      setForm({ name: '', category: '', unit: '', price: 0, min_stock: 0, is_regular: false });
      loadItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal menambahkan barang');
    }
  };

  const handleDownloadTemplate = () => {
    // Create sample data
    const template = [
      { name: 'Contoh: Bohlam LED 10W', category: 'elektrikal', unit: 'pcs', price: 25000, min_stock: 10, stock: 0 },
      { name: 'Contoh: Kabel Listrik 2.5mm', category: 'elektrikal', unit: 'meter', price: 8000, min_stock: 50, stock: 0 },
      { name: 'Contoh: Cat Tembok 5L', category: 'material', unit: 'kaleng', price: 350000, min_stock: 5, stock: 0 },
    ];
    const ws = XLSX.utils.json_to_sheet(template);
    // Set column widths
    ws['!cols'] = [
      { wch: 35 }, // name
      { wch: 15 }, // category
      { wch: 10 }, // unit
      { wch: 12 }, // price
      { wch: 12 }, // min_stock
      { wch: 10 }, // stock
    ];
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Items');
    XLSX.writeFile(wb, 'template_master_barang.xlsx');
    toast.success('Template berhasil didownload');
  };

  const handleFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const data = new Uint8Array(evt.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const rows = XLSX.utils.sheet_to_json(worksheet);

        // Validate and normalize
        const parsed = rows
          .filter((r) => r.name && !String(r.name).toLowerCase().startsWith('contoh'))
          .map((r) => ({
            name: String(r.name || '').trim(),
            category: String(r.category || 'lainnya').toLowerCase().trim(),
            unit: String(r.unit || 'pcs').trim(),
            price: parseFloat(r.price) || 0,
            min_stock: parseInt(r.min_stock) || 0,
            stock: parseInt(r.stock) || 0,
          }));

        if (parsed.length === 0) {
          toast.error('Tidak ada data valid di file Excel');
          return;
        }

        setPreviewItems(parsed);
        toast.success(`${parsed.length} baris siap di-import`);
      } catch (error) {
        toast.error('Format file tidak valid. Gunakan template XLSX.');
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleBulkImport = async () => {
    if (previewItems.length === 0) {
      toast.error('Upload file dulu');
      return;
    }
    setUploading(true);
    try {
      const { data } = await api.post('/items/bulk', { items: previewItems });
      toast.success(data.message);
      if (data.errors?.length > 0) {
        console.warn('Import errors:', data.errors);
      }
      setShowBulkDialog(false);
      setPreviewItems([]);
      if (fileInputRef.current) fileInputRef.current.value = '';
      loadItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Gagal import barang');
    } finally {
      setUploading(false);
    }
  };

  const canManage = hasPermission('manage_items');

  return (
    <Layout>
      <div className="space-y-6" data-testid="items-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] font-bold text-muted-foreground mb-2">Master Data</p>
            <h1 className="text-3xl sm:text-4xl font-medium tracking-tight">Barang Maintenance</h1>
            <p className="text-muted-foreground mt-1 text-sm sm:text-base">Kelola master data barang maintenance</p>
          </div>
          {canManage && (
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleDownloadTemplate}
                data-testid="download-template-button"
              >
                <Download className="w-4 h-4 mr-2" />
                Template Excel
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowBulkDialog(true)}
                data-testid="bulk-upload-button"
              >
                <FileSpreadsheet className="w-4 h-4 mr-2" />
                Import Excel
              </Button>
              <Button
                onClick={() => setShowDialog(true)}
                className="bg-primary hover:bg-primary/90"
                size="sm"
                data-testid="add-item-button"
              >
                <Plus className="w-4 h-4 mr-2" />
                Tambah
              </Button>
            </div>
          )}
        </div>

        {/* Filters & Sort */}
        <Card className="border-border">
          <CardContent className="p-4 grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Cari</Label>
              <Input
                placeholder="Nama / satuan..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="h-9"
                data-testid="items-search-input"
              />
            </div>
            <div>
              <Label className="text-xs">Kategori</Label>
              <Select value={filterCategory} onValueChange={setFilterCategory}>
                <SelectTrigger className="h-9" data-testid="items-filter-category"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Semua Kategori</SelectItem>
                  {ITEM_CATEGORIES.map((c) => <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Urutkan</Label>
              <Select value={sortBy} onValueChange={setSortBy}>
                <SelectTrigger className="h-9" data-testid="items-sort-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="name_asc">Nama A→Z</SelectItem>
                  <SelectItem value="name_desc">Nama Z→A</SelectItem>
                  <SelectItem value="price_asc">Harga terendah</SelectItem>
                  <SelectItem value="price_desc">Harga tertinggi</SelectItem>
                  <SelectItem value="stock_asc">Stok paling sedikit</SelectItem>
                  <SelectItem value="stock_desc">Stok paling banyak</SelectItem>
                  <SelectItem value="new_first">Barang baru dulu</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
          <div className="border-t border-border px-4 py-2 flex items-center gap-2">
            <label className="flex items-center gap-2 text-xs cursor-pointer" data-testid="items-show-all-label">
              <input
                type="checkbox"
                checked={showAllItems}
                onChange={(e) => setShowAllItems(e.target.checked)}
                data-testid="items-show-all-checkbox"
              />
              Tampilkan barang non-regular yang stoknya 0 (default: disembunyikan)
            </label>
          </div>
        </Card>

        <Card className="border-border">
          <CardContent className="p-0">
            {loading ? (
              <div className="p-12 text-center">
                <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-primary border-r-transparent"></div>
              </div>
            ) : items.length === 0 ? (
              <div className="p-12 text-center text-muted-foreground">
                <Box className="w-16 h-16 mx-auto mb-4 opacity-30" />
                <p>Belum ada barang. Klik &quot;Import Excel&quot; untuk import massal.</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-secondary/50 border-b border-border">
                    <tr>
                      <th className="text-left px-4 sm:px-6 py-3 font-semibold text-xs uppercase tracking-wider text-muted-foreground">Nama</th>
                      <th className="text-left px-4 sm:px-6 py-3 font-semibold text-xs uppercase tracking-wider text-muted-foreground hidden sm:table-cell">Kategori</th>
                      <th className="text-left px-4 sm:px-6 py-3 font-semibold text-xs uppercase tracking-wider text-muted-foreground hidden md:table-cell">Satuan</th>
                      <th className="text-right px-4 sm:px-6 py-3 font-semibold text-xs uppercase tracking-wider text-muted-foreground">Harga</th>
                      <th className="text-right px-4 sm:px-6 py-3 font-semibold text-xs uppercase tracking-wider text-muted-foreground">Stok</th>
                      <th className="text-right px-4 sm:px-6 py-3 font-semibold text-xs uppercase tracking-wider text-muted-foreground hidden md:table-cell">Min</th>
                      <th className="text-right px-4 sm:px-6 py-3 font-semibold text-xs uppercase tracking-wider text-muted-foreground">Aksi</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-border">
                    {items
                      .filter((item) => {
                        const q = searchQuery.trim().toLowerCase();
                        if (filterCategory !== 'all' && item.category !== filterCategory) return false;
                        if (q && !(`${item.name || ''} ${item.unit || ''}`.toLowerCase().includes(q))) return false;
                        return true;
                      })
                      .sort((a, b) => {
                        const stockA = stockByItem[a.id]?.total || 0;
                        const stockB = stockByItem[b.id]?.total || 0;
                        switch (sortBy) {
                          case 'name_asc': return (a.name || '').localeCompare(b.name || '');
                          case 'name_desc': return (b.name || '').localeCompare(a.name || '');
                          case 'price_asc': return (a.price || 0) - (b.price || 0);
                          case 'price_desc': return (b.price || 0) - (a.price || 0);
                          case 'stock_asc': return stockA - stockB;
                          case 'stock_desc': return stockB - stockA;
                          case 'new_first':
                            if (a.is_new && !b.is_new) return -1;
                            if (!a.is_new && b.is_new) return 1;
                            return (a.name || '').localeCompare(b.name || '');
                          default: return 0;
                        }
                      })
                      .map((item) => (
                      <tr key={item.id} className={`hover:bg-secondary/30 ${item.is_new ? 'bg-amber-50/40' : ''}`} data-testid={`item-row-${item.id}`}>
                        <td className="px-4 sm:px-6 py-3">
                          <div className="flex items-center gap-2 flex-wrap">
                            <p className="font-medium">{item.name}</p>
                            {item.is_new && (
                              <Badge
                                className="bg-amber-100 text-amber-800 border-amber-300 text-[10px] px-1.5 py-0 h-5"
                                data-testid={`item-new-badge-${item.id}`}
                              >
                                <Sparkles className="w-2.5 h-2.5 mr-1" />
                                BARU
                              </Badge>
                            )}
                          </div>
                          <p className="text-xs text-muted-foreground sm:hidden capitalize">
                            {item.category} • {item.unit}
                          </p>
                        </td>
                        <td className="px-4 sm:px-6 py-3 text-muted-foreground capitalize hidden sm:table-cell">{item.category}</td>
                        <td className="px-4 sm:px-6 py-3 hidden md:table-cell">{item.unit}</td>
                        <td className="px-4 sm:px-6 py-3 text-right whitespace-nowrap">{formatCurrency(item.price)}</td>
                        <td className="px-4 sm:px-6 py-3 text-right whitespace-nowrap">
                          {(() => {
                            const s = stockByItem[item.id];
                            const total = s?.total || 0;
                            const isLow = total < (item.min_stock || 0);
                            const locations = s ? Object.entries(s.byLocation) : [];
                            return (
                              <div className="flex flex-col items-end gap-0.5">
                                <span className={`font-semibold ${total === 0 ? 'text-muted-foreground' : isLow ? 'text-destructive' : 'text-foreground'}`}>
                                  {total} {item.unit}
                                </span>
                                {locations.length > 0 && (
                                  <span className="text-[10px] text-muted-foreground">
                                    {locations.map(([loc, qty]) => `${loc}: ${qty}`).join(' • ')}
                                  </span>
                                )}
                                {isLow && total > 0 && (
                                  <span className="text-[10px] text-destructive font-medium">Di bawah min</span>
                                )}
                              </div>
                            );
                          })()}
                        </td>
                        <td className="px-4 sm:px-6 py-3 text-right hidden md:table-cell">{item.min_stock}</td>
                        <td className="px-4 sm:px-6 py-3 text-right whitespace-nowrap">
                          <div className="flex items-center justify-end gap-2">
                            {item.is_new && canManage && (
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleMarkReviewed(item.id, item.name)}
                                className="h-7 text-xs"
                                data-testid={`item-review-button-${item.id}`}
                              >
                                <Check className="w-3 h-3 mr-1" />
                                Sudah Review
                              </Button>
                            )}
                            {canDeleteItems && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleDeleteItem(item.id, item.name)}
                                className="h-7 w-7 p-0 text-destructive hover:bg-destructive/10 hover:text-destructive"
                                data-testid={`item-delete-button-${item.id}`}
                                title="Hapus barang"
                              >
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            )}
                            {!item.is_new && !canDeleteItems && (
                              <span className="text-muted-foreground text-xs">—</span>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Single Add Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Tambah Barang Baru</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="space-y-2">
                <Label>Nama Barang *</Label>
                <Input
                  placeholder="Contoh: Kabel Listrik 2.5mm"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  data-testid="item-name-input"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Kategori *</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger data-testid="item-category-select">
                      <SelectValue placeholder="Pilih" />
                    </SelectTrigger>
                    <SelectContent>
                      {ITEM_CATEGORIES.map((c) => (
                        <SelectItem key={c.value} value={c.value}>{c.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Satuan *</Label>
                  <Input
                    placeholder="pcs, meter, kg"
                    value={form.unit}
                    onChange={(e) => setForm({ ...form, unit: e.target.value })}
                    data-testid="item-unit-input"
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Harga (Rp) *</Label>
                  <Input
                    type="number"
                    min="0"
                    value={form.price}
                    onChange={(e) => setForm({ ...form, price: e.target.value })}
                    data-testid="item-price-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Stok Minimum</Label>
                  <Input
                    type="number"
                    min="0"
                    value={form.min_stock}
                    onChange={(e) => setForm({ ...form, min_stock: e.target.value })}
                    data-testid="item-min-stock-input"
                  />
                </div>
              </div>
              <label className="flex items-start gap-2 p-3 rounded-md border border-border bg-secondary/40 cursor-pointer" data-testid="item-regular-label">
                <input
                  type="checkbox"
                  checked={form.is_regular}
                  onChange={(e) => setForm({ ...form, is_regular: e.target.checked })}
                  className="mt-1"
                  data-testid="item-regular-checkbox"
                />
                <div>
                  <p className="text-sm font-medium">Barang Regular</p>
                  <p className="text-xs text-muted-foreground">
                    Kalau dicentang, barang tetap tampil di Master Barang & Inventori meski stok = 0.
                    Kalau tidak, barang otomatis disembunyikan saat stok mencapai 0 (riwayat tetap tersimpan).
                  </p>
                </div>
              </label>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowDialog(false)}>Batal</Button>
              <Button onClick={handleCreate} className="bg-primary hover:bg-primary/90" data-testid="save-item-button">
                Simpan
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Bulk Upload Dialog */}
        <Dialog open={showBulkDialog} onOpenChange={setShowBulkDialog}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Import Barang dari Excel</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div className="p-4 bg-primary/5 border border-primary/20 rounded-lg">
                <p className="text-sm mb-2">
                  <strong>Langkah:</strong>
                </p>
                <ol className="text-sm space-y-1 list-decimal list-inside text-muted-foreground">
                  <li>Download template Excel</li>
                  <li>Isi data barang (hapus baris &quot;Contoh:&quot;)</li>
                  <li>Kolom: <span className="font-mono text-xs">name, category, unit, price, min_stock, stock</span></li>
                  <li>Upload file di bawah</li>
                </ol>
                <p className="text-xs text-muted-foreground mt-2">
                  Kolom <span className="font-mono">stock</span> opsional — jika diisi, stok awal otomatis masuk ke Gudang Pusat. Untuk distribusi ke cabang, gunakan Transfer Stok setelah import.
                </p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDownloadTemplate}
                  className="mt-3"
                >
                  <Download className="w-3 h-3 mr-2" />
                  Download Template
                </Button>
              </div>

              <div className="space-y-2">
                <Label>Upload File Excel</Label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept=".xlsx,.xls,.csv"
                  onChange={handleFileChange}
                  className="block w-full text-sm text-foreground file:mr-4 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:font-semibold file:bg-primary file:text-primary-foreground hover:file:bg-primary/90 cursor-pointer"
                  data-testid="bulk-file-input"
                />
              </div>

              {previewItems.length > 0 && (
                <div className="border border-border rounded-lg overflow-hidden">
                  <div className="p-3 bg-secondary/50 border-b border-border flex items-center justify-between">
                    <p className="text-sm font-semibold">Preview Data</p>
                    <span className="text-xs text-muted-foreground">{previewItems.length} barang</span>
                  </div>
                  <div className="max-h-64 overflow-y-auto">
                    <table className="w-full text-sm">
                      <thead className="bg-muted/50 sticky top-0">
                        <tr>
                          <th className="text-left px-3 py-2 text-xs font-semibold">Nama</th>
                          <th className="text-left px-3 py-2 text-xs font-semibold">Kategori</th>
                          <th className="text-right px-3 py-2 text-xs font-semibold">Harga</th>
                          <th className="text-right px-3 py-2 text-xs font-semibold">Stok</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-border">
                        {previewItems.slice(0, 50).map((it, idx) => (
                          <tr key={idx}>
                            <td className="px-3 py-2">{it.name}</td>
                            <td className="px-3 py-2 text-muted-foreground capitalize">{it.category}</td>
                            <td className="px-3 py-2 text-right">{formatCurrency(it.price)}</td>
                            <td className="px-3 py-2 text-right">{it.stock || 0}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {previewItems.length > 50 && (
                      <p className="p-2 text-center text-xs text-muted-foreground">
                        ...dan {previewItems.length - 50} lainnya
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setShowBulkDialog(false)}>Batal</Button>
              <Button
                onClick={handleBulkImport}
                disabled={uploading || previewItems.length === 0}
                className="bg-primary hover:bg-primary/90"
                data-testid="import-bulk-button"
              >
                {uploading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Import...
                  </>
                ) : (
                  <>
                    <Upload className="w-4 h-4 mr-2" />
                    Import {previewItems.length} Barang
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default ItemsPage;
